/**
 * How a fresh map-layer review starts from the colours its file was drawn in.
 *
 * The converter writes the colour an imported DXF/KML feature was drawn in onto
 * `sourceColor`, a polygon's separate fill onto `sourceFillColor`, and lists a
 * sublayer's distinct colours in the version inventory. This module holds the
 * decisions that follow from those lists, kept free of Angular and of
 * @arcgis/core so they can be unit tested on their own.
 */

/** Machine properties describing how a feature was drawn, not what it is. */
export const SOURCE_COLOR_FIELD = 'sourceColor';
export const SOURCE_FILL_COLOR_FIELD = 'sourceFillColor';
export const SOURCE_STYLE_FIELDS = ['sourceStyle', SOURCE_COLOR_FIELD, SOURCE_FILL_COLOR_FIELD];

/** How the Color by picker and the legend heading name the file's own colours. */
export const SOURCE_COLOR_FIELD_LABEL = 'Colours from the file';

/**
 * The file's own colours are one per CAD layer or KML folder, more than the two
 * dozen an ordinary field may have and still read. The converter caps its own
 * inventory at the same number.
 */
export const MAX_SOURCE_COLOR_VALUES = 64;

export const TOO_MANY_SOURCE_COLORS_HINT =
  'Too many colours in this file to keep them all; using the most common one';

/**
 * Pure black and pure white are what CAD's default colour 7 and an uncoloured
 * drawing come through as, so a file holding nothing else never really chose a
 * colour. A file that does choose colours has chosen these like any other.
 */
const UNAUTHORED_SOURCE_COLORS = ['#000000', '#ffffff'];

const HEX_COLOR = /^#[0-9a-f]{6}$/;

/** A usable lowercase `#rrggbb`, or '' for anything else. */
export function sourceHexColor(value: unknown): string {
  const color = typeof value === 'string' ? value.trim().toLowerCase() : '';
  return HEX_COLOR.test(color) ? color : '';
}

export interface SourceColorDefaultInput {
  /** Distinct colours, first appearance first; the inventory caps this list. */
  colors: string[];
  /** Every distinct colour the file holds, which may exceed what `colors` lists. */
  total?: number;
  /** The most symbol families any selected sublayer needs. */
  families: number;
  /** The most unique-value symbols one prepared layer may carry. */
  symbolBudget: number;
  /** The colour the most features carry, once the source itself has been read. */
  dominant?: string;
}

export type SourceColorDefault =
  /** Nothing the file says is worth starting from; keep the SiteNotes colour. */
  | { kind: 'none' }
  /** One colour, which needs no legend and becomes the layer colour. */
  | { kind: 'layer-color'; color: string }
  /** Colour by `sourceColor`, one legend entry per colour. */
  | { kind: 'color-by'; colors: string[] }
  /** More colours than one prepared layer can carry; keep the commonest. */
  | { kind: 'dominant-color'; color: string; hint: string };

/**
 * What a fresh review should do with the colours a file authored.
 *
 * `total` decides, not `colors.length`: the inventory stops listing colours at
 * its own cap, so a file with more than the cap would otherwise look as though
 * it fitted, seed a colour-by that the live source then contradicts, and leave
 * the reviewer with a plain layer and no explanation.
 */
export function sourceColorDefault(input: SourceColorDefaultInput): SourceColorDefault {
  const colors: string[] = [];
  for (const value of input.colors || []) {
    const color = sourceHexColor(value);
    if (color && !colors.includes(color)) colors.push(color);
  }
  if (!colors.length) return { kind: 'none' };
  const total = Math.max(Number.isFinite(input.total) ? input.total : 0, colors.length);
  if (total === 1) {
    return UNAUTHORED_SOURCE_COLORS.includes(colors[0]) ? { kind: 'none' } : { kind: 'layer-color', color: colors[0] };
  }
  const families = Math.max(1, input.families || 1);
  if (total > MAX_SOURCE_COLOR_VALUES || colors.length * families > input.symbolBudget) {
    return { kind: 'dominant-color', color: sourceHexColor(input.dominant) || colors[0], hint: TOO_MANY_SOURCE_COLORS_HINT };
  }
  return { kind: 'color-by', colors };
}

/**
 * What the legend calls one of the file's colours: the CAD layer or KML folder
 * that uses it, the first three when several share it, and the hex itself when
 * the source has not been read closely enough to name one.
 */
export function sourceColorLabel(color: string, names: string[]): string {
  const unique: string[] = [];
  for (const name of names || []) {
    const trimmed = typeof name === 'string' ? name.trim() : '';
    if (trimmed && !unique.includes(trimmed)) unique.push(trimmed);
  }
  if (!unique.length) return color;
  if (unique.length === 1) return unique[0];
  const shown = unique.slice(0, 3).join(', ');
  return unique.length > 3 ? `${shown} +${unique.length - 3} more` : shown;
}
